// SPDX-License-Identifier: AGPL-3.0-or-later

import {clampToRoom} from '@app/features/voice_scene/net/RoomSignals';
import type {RoomCutaway} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {ROOM_LIMITS, type RoomPose} from '@fluxer/constants/src/RoomCharacters';

const PACKET_MS = 1000 / ROOM_LIMITS.posSendHz;
// Remote walkers are drawn 1.2 packet intervals behind their newest packet, so there are two packets to move between
// (120 ms at 10 a second).
export const POSITION_DELAY_MS = Math.round(1.2 * PACKET_MS);
// With no newer packet, a moving walker carries on at its last speed for three intervals at most, then stops.
export const POSITION_PREDICT_MS = Math.round(3 * PACKET_MS);
// Silent this long (a lost heartbeat and then some), a walker is back in its seat.
export const POSITION_STALE_MS = ROOM_LIMITS.posIdleHeartbeatS * 2500;
const SAMPLES_KEPT = 4;
const TURN = 2 * Math.PI;

interface Sample {
	at: number;
	pose: RoomPose;
}

const isMoving = (pose: RoomPose): boolean => pose.anim === 'walk' || pose.anim === 'run';
const blank = (): RoomPose => ({x: 0, y: 0, z: 0, yaw: 0, anim: 'idle'});

function assign(out: RoomPose, x: number, y: number, z: number, yaw: number, anim: RoomPose['anim']): RoomPose {
	out.x = x;
	out.y = y;
	out.z = z;
	out.yaw = yaw;
	out.anim = anim;
	return out;
}

const copy = (out: RoomPose, pose: RoomPose): RoomPose => assign(out, pose.x, pose.y, pose.z, pose.yaw, pose.anim);

function between(a: RoomPose, b: RoomPose, f: number, out: RoomPose): RoomPose {
	const turn = ((((b.yaw - a.yaw) % TURN) + TURN + Math.PI) % TURN) - Math.PI;
	return assign(
		out,
		a.x + (b.x - a.x) * f,
		a.y + (b.y - a.y) * f,
		a.z + (b.z - a.z) * f,
		a.yaw + turn * f,
		isMoving(b) ? b.anim : a.anim,
	);
}

// One remote walker's last few packets, by the time they arrived (performance.now()).
export class PositionBuffer {
	private samples: Array<Sample> = [];

	push(pose: RoomPose, at: number): void {
		const newest = this.samples[this.samples.length - 1];
		if (newest && at <= newest.at) return;
		if (newest && at - newest.at > POSITION_STALE_MS) this.samples = [];
		this.samples.push({at, pose});
		if (this.samples.length > SAMPLES_KEPT) this.samples.shift();
	}

	// Where to draw the walker now, written into `out` (a new pose without one); null once it has gone quiet (back in
	// its seat).
	// bounds: the room's walls; a walker carried on past its newest packet is kept inside them and on the floor.
	sample(now: number, out: RoomPose = blank(), bounds: RoomCutaway | null = null): RoomPose | null {
		const {samples} = this;
		const newest = samples[samples.length - 1];
		if (!newest || now - newest.at > POSITION_STALE_MS) return null;
		const t = now - POSITION_DELAY_MS;
		if (t < samples[0]!.at) return copy(out, samples[0]!.pose);
		for (let i = samples.length - 1; i > 0; i--) {
			const a = samples[i - 1]!;
			const b = samples[i]!;
			if (t >= a.at && t < b.at) return between(a.pose, b.pose, (t - a.at) / (b.at - a.at), out);
		}
		const previous = samples[samples.length - 2];
		if (!previous || !isMoving(newest.pose)) return copy(out, newest.pose);
		const ahead = Math.min(t - newest.at, POSITION_PREDICT_MS) / (newest.at - previous.at);
		const {pose} = newest;
		assign(
			out,
			pose.x + (pose.x - previous.pose.x) * ahead,
			pose.y + (pose.y - previous.pose.y) * ahead,
			pose.z + (pose.z - previous.pose.z) * ahead,
			pose.yaw,
			pose.anim,
		);
		if (!bounds) return out;
		const inside = clampToRoom(out, bounds);
		return assign(out, inside.x, inside.y, inside.z, out.yaw, out.anim);
	}
}

// The walkers of the room this client is in, by LiveKit identity. useRoomSignals fills it with packets that passed its
// filters; the character layer reads pose() each frame and draws anyone without one in their seat. Only this client,
// only this session, never stored or sent anywhere.
export class RemotePositions {
	private readonly buffers = new Map<string, PositionBuffer>();
	private readonly listeners = new Set<() => void>();
	private bounds: RoomCutaway | null = null;

	// The room's walls while walking is on (useRoomSignals): a walker carried on is kept inside them.
	setBounds(bounds: RoomCutaway | null): void {
		this.bounds = bounds;
	}

	// Every accepted packet, and every walker forgotten, gone quiet or cleared: the room steps its characters.
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	private changed(): void {
		for (const listener of this.listeners) listener();
	}

	get size(): number {
		return this.buffers.size;
	}

	push(identity: string, pose: RoomPose, at: number): void {
		let buffer = this.buffers.get(identity);
		if (!buffer) {
			buffer = new PositionBuffer();
			this.buffers.set(identity, buffer);
		}
		buffer.push(pose, at);
		this.changed();
	}

	// `out`, when given, is filled instead of a new pose.
	pose(identity: string, now: number, out?: RoomPose): RoomPose | null {
		const buffer = this.buffers.get(identity);
		if (!buffer) return null;
		const pose = buffer.sample(now, out, this.bounds);
		if (!pose) {
			this.buffers.delete(identity);
			this.changed();
		}
		return pose;
	}

	forget(identity: string): void {
		if (this.buffers.delete(identity)) this.changed();
	}

	clear(): void {
		if (this.buffers.size === 0) return;
		this.buffers.clear();
		this.changed();
	}
}

export const RoomPositions = new RemotePositions();
